import React, { useState } from "react";
import { Box, TextField, Stack, Button } from "@mui/material";
import { useUsersQuery } from "Clients/Api/Queries/useUsersQuery";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { AppRouting, getPath } from "Infrastructure/Utils/UrlUtils";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import { ExpandArrowIcon } from "Shared/Icons";
import AddIcon from "@mui/icons-material/Add";
import { BlDefaultButton } from "Shared/Buttons/BlDefaultButton";
import { UserListItemDto } from "Infrastructure/Api/Api";
import useDebounce from "Infrastructure/Hooks/useDebounce";

const StyledButton = styled(BlDefaultButton)`
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;

	svg {
		color: ${(props) => props.theme.palette.text.secondary};
		width: 12px;
		height: auto;
		transform: rotate(-90deg);
	}
`;

const StyledCell = styled(Box)``;

type Props = {};

export const UsersTable: React.FunctionComponent<Props> = () => {
	const { data, isLoading } = useUsersQuery();
	const [filter, setFilter] = useState("");
	const debouncedFilter = useDebounce(filter, 300);

	const clients: UserListItemDto[] =
		data?.status === 200 ? data.data.items : [];

	const filteredClients = clients.filter((user) =>
		user.login.toLowerCase().includes(debouncedFilter.toLowerCase())
	);

	const navigate = useNavigate();

	return (
		<>
			<Stack direction="row" justifyContent="space-between" spacing={2} mb={2}>
				<Button
					color="primary"
					variant="outlined"
					endIcon={<AddIcon />}
					onClick={(_e) => {
						navigate(getPath(AppRouting.UserNew));
					}}
				>
					{"Nový"}
				</Button>
				<TextField
					label="Hledat"
					variant="outlined"
					size="small"
					value={filter}
					onChange={(e) => setFilter(e.target.value)}
					sx={{ width: "350px" }}
				/>
			</Stack>
			<BlDataTable
				rows={filteredClients.map((x) => ({ data: x }))}
				rowEntityID="userID"
				isLoading={isLoading}
				getHeaderCell={(column) => <div>{column.label ?? column.id}</div>}
				columns={[
					{ id: "login", label: " Login" },
					{ id: "lastName", label: "Příjmení" },
					{ id: "firstName", label: "Jméno" },
					{ id: "accessRoles", label: "Role" },
					{ id: "status", label: "Stav" },
					{ id: "action", label: "", maxWidth: 0, justifySelf: "flex-end" },
				]}
				rowsPerPage={10}
				onRowClick={(row) => {
					navigate(getPath(AppRouting.UserDetail, row.userID));
				}}
				getCell={(id, data) => {
					switch (id) {
						case "userID":
							return <StyledCell>{data.userID}</StyledCell>;
						case "login":
							return <StyledCell>{data.login}</StyledCell>;
						case "firstName":
							return <StyledCell>{data.firstName}</StyledCell>;
						case "lastName":
							return <StyledCell>{data.lastName}</StyledCell>;
						case "status":
							return <StyledCell>{data.status}</StyledCell>;
						case "accessRoles":
							return (
								<StyledCell>
									{data.accessRoles.map((x) => x.name).join(", ")}
								</StyledCell>
							);
						case "action":
							return (
								<StyledCell>
									<UnstyledLink
										to={getPath(AppRouting.UserDetail, data.userID)}
									>
										<StyledButton>
											<ExpandArrowIcon />
										</StyledButton>
									</UnstyledLink>
								</StyledCell>
							);
						default:
							return <StyledCell>{data[id]}</StyledCell>;
					}
				}}
			/>
		</>
	);
};
